import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useAvContext } from '../../../../context/AvContextProvider';
import { useRunAggregation } from '../../../ModelManagement/hooks';
import { CREATE_OR_UPDATE_AUTOMATION } from '../../TicketLifecycle/hooks';
import { ISeverityAutomationType } from './types';

const GET_SEVERITY_SETTINGS = gql`
  query getAutomationForAutomationTypeAndProjection($type: AutomationType!, $projectionName: String!) {
    getAutomationForAutomationTypeAndProjection(type: $type, projectionName: $projectionName) {
      id
      automationType
      projectionName
      automationRules {
        id
        name
        ruleOrder
        config
      }
    }
  }
`;
export const useGetSeveritySettings = (
  { projectionName, onSuccess }: { projectionName: string; onSuccess?: (data: ISeverityAutomationType) => void } = {
    projectionName: 'tickets',
  }
) =>
  useGqlQuery<ISeverityAutomationType>({
    queryKey: [QueryKey.SeveritySettings, projectionName],
    query: GET_SEVERITY_SETTINGS,
    variables: {
      projectionName,
      type: 'SEVERITY',
    },
    onSuccess,
  });
export const useCreateOrUpdateAutomation = ({ onSuccess }: { onSuccess?: () => void }) => {
  const {
    api,
    accountEntities: { aggProjs },
  } = useAvContext();
  const { mutate: runAggregation } = useRunAggregation();

  return useMutation({
    mutationFn: ({ automation }: { automation: ISeverityAutomationType; shouldRun: boolean }) => {
      const automationRulesToSave = automation.automationRules.map(rule => {
        if (rule.isNew) {
          const { isNew, ...rest } = rule;
          return { ...rest, id: null };
        }
        return rule;
      });
      return api(CREATE_OR_UPDATE_AUTOMATION, {
        options: {
          automationDto: { ...automation, automationRules: automationRulesToSave },
        },
      });
    },
    onSuccess: (_, { shouldRun }) => {
      onSuccess?.();
      if (shouldRun) {
        runAggregation({ projIds: [aggProjs.uber_findings.projId] });
      }
    },
  });
};
const GET_SEVERITY_SETTINGS_DEFAULTS = gql`
  query getAutomationTemplates($type: AutomationType!, $projectionName: String!) {
    getAutomationTemplates(type: $type, projectionName: $projectionName) {
      default
      automationDto {
        id
        automationType
        projectionName
        automationRules {
          id
          name
          ruleOrder
          config
        }
      }
    }
  }
`;

export const useGetSeveritySettingsDefaults = ({ projectionName }: { projectionName: string }) =>
  useGqlQuery<ISeverityAutomationType>({
    queryKey: [QueryKey.SeveritySettingsDefaults],
    query: GET_SEVERITY_SETTINGS_DEFAULTS,
    variables: {
      projectionName,
      type: 'SEVERITY',
    },
    select: data => data[0].automationDto,
  });
